import { useState } from 'react'
import { ArrowRight, Play } from 'lucide-react'
import { useRailScroll } from '@/hooks/useRailScroll'
import RailArrow from './RailArrow'
import { BEWAVE_YOUTUBE_SHORTS_URL, PUBLIC_CONTAINER } from './publicShell'
import { cn } from '@/lib/utils'

const HEADING_ID = 'surf-shorts-heading'
const RAIL_ID = 'trilho-shorts'

interface Short {
  /** ID do vídeo no YouTube. É a chave da lista e o que monta o embed. */
  id: string
  /** Vira o `alt` da capa, o rótulo do botão e o `title` do iframe. */
  title: string
}

/**
 * Os shorts do canal, do mais novo para o mais antigo — a mesma ordem da aba
 * /shorts do YouTube. Quem chega vê primeiro o que acabou de sair.
 *
 * Lista à mão de propósito. O YouTube não publica embed de "os shorts do canal":
 * ou se lê o RSS (que mistura short com vídeo comum e não distingue os dois), ou
 * se chama a Data API v3, que quer chave, requisição no servidor e cache. Com meia
 * dúzia de vídeos, escolher os bons vende mais do que mostrar os últimos — e a
 * lista fica sob controle de quem escreve a landing.
 *
 * Para trocar: pegue o ID da URL (`youtube.com/shorts/<ID>`), gere a capa vertical
 * em `public/lp/shorts/<ID>.webp` e acrescente aqui. As capas são locais, e não do
 * i.ytimg.com, por dois motivos: o `oardefault.jpg` (o único recorte vertical que
 * o YouTube expõe) não existe para todo short — devolve 404 e cai num quadrado
 * cinza —, e o `maxresdefault.jpg` vem 16:9 com tarja borrada dos lados.
 *
 * Como gerar a capa: baixe `i.ytimg.com/vi/<ID>/maxresdefault.jpg` (1280×720),
 * recorte os 405 px do centro — a tarja dos lados é a mesma imagem escurecida, e
 * o miolo é o quadro vertical intacto — e salve em webp 405×720. O `frame0.jpg`
 * já vem vertical, mas com 268 px de largura fica mole na moldura do card.
 */
const SHORTS: Short[] = [
  {
    id: 'IWM2I3-oeac',
    title: 'O feedback sincero de uma cliente BeWave',
  },
  {
    id: 'KOPSr27oNh0',
    title: 'Quem está dentro d’água também sente quando a gestão flui',
  },
  {
    id: 'UprAiUy-3J0',
    title: 'Menos tempo na gestão, mais tempo na água',
  },
  {
    id: 'krTxV79lbH0',
    title: 'Um dia na Fassola Surf School',
  },
  {
    id: 'tql01tLFk2o',
    title: '"Hoje tudo ficou muito mais fácil" — depoimento real de aluno',
  },
  {
    id: 'cPlfwAC1cWA',
    title: 'BeWave no Junco Session Festival, na Praia do Futuro',
  },
  {
    id: 'rlKULTfnb8s',
    title: 'BeWave visita a escola Junco na Praia do Futuro',
  },
  {
    id: 'aBevFZP48IY',
    title: 'Acompanhando uma manhã com a escola Expresso Surf no Titanzinho',
  },
]

/**
 * A mesma fileira de vídeos serve duas páginas que vendem coisas diferentes, e
 * por isso o texto muda com a página.
 *
 * Na landing (`bewave.surf`) o assunto é a BeWave: quem chega ali está avaliando
 * o produto, e o vídeo mostra a gente na praia com as escolas. No diretório
 * (`explore.bewave.surf`) o assunto são os NEGÓCIOS que estão nele, e não só
 * escola de surf — o diretório tem shaper, fotógrafo, beach club, academia. O
 * texto de lá fala com os dois lados do produto ao mesmo tempo: quem procura vê
 * o lugar antes de chegar, e quem ainda não está no diretório vê o que ganha
 * entrando (é o mesmo argumento do `ExploreCtaBanner`). O `accent`
 * também troca de azul: o diretório usa o azul do tema (`primary`, o mesmo dos
 * títulos de seção de lá), a landing usa o `lp-blue` do Figma da landing.
 */
const COPY = {
  landing: {
    eyebrow: 'No nosso YouTube',
    heading: {
      dark: 'Acompanhe o dia a dia da',
      accent: 'BeWave na praia.',
    },
    lead: 'Nosso canal reúne visitas, aulas, bastidores e histórias da comunidade do surf.',
    accentClass: 'text-lp-blue',
  },
  explore: {
    eyebrow: 'O Explore por dentro',
    heading: {
      dark: 'A gente vai até',
      accent: 'quem está no Explore.',
    },
    lead: 'Escola de surf, shaper, fotógrafo, beach club: filmamos os negócios do diretório trabalhando, para você ver o lugar e a gente antes de chegar lá. O próximo pode ser o seu.',
    accentClass: 'text-primary',
  },
} as const

export type SurfShortsVariant = keyof typeof COPY

interface SurfShortsProps {
  /**
   * Qual página está mostrando a seção. Muda só o texto e o azul do destaque —
   * a fileira de vídeos é a mesma nas duas.
   */
  variant?: SurfShortsVariant
}

/** `youtube-nocookie` não planta cookie de rastreio antes de o vídeo tocar. */
function embedSrc(id: string): string {
  return `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&playsinline=1`
}

function coverSrc(id: string): string {
  return `/lp/shorts/${id}.webp`
}

/**
 * Os shorts do canal, logo depois da seção do Explore.
 *
 * Fica aqui porque é a prova do que a seção de cima promete: o texto diz que a
 * gente conversa com cada escola antes de publicar, e o vídeo mostra a gente na
 * praia com ela. Argumento e evidência coladas.
 *
 * Nenhum iframe nasce com a página. Um embed do YouTube passa de 1 MB e é a coisa
 * mais cara da landing — numa página que recebe tráfego pago, isso sai do bolso
 * duas vezes: no LCP e na conta do anúncio. O que carrega é uma capa de 23 kB, e
 * o player só entra quando alguém clica.
 */
export default function SurfShorts({ variant = 'landing' }: SurfShortsProps) {
  const copy = COPY[variant]

  // Um por vez: dois iframes tocando juntos viram dois áudios sobrepostos, e
  // quem clicou no segundo não faz ideia de onde vem o primeiro.
  const [playingId, setPlayingId] = useState<string | null>(null)

  // Mesma mecânica dos trilhos do diretório (fileiras da vista lista, fila de
  // serviços do header): setas de card em card, arrasto com o mouse e o clique
  // do fim do arrasto engolido. Antes isto era uma cópia local só desta seção.
  const { ref, dragProps, canScrollPrev, canScrollNext, scrollByPage } =
    useRailScroll<HTMLUListElement>({ id: RAIL_ID })

  const hasControls = canScrollPrev || canScrollNext

  return (
    <section className="w-full py-12 lg:py-20" aria-labelledby={HEADING_ID}>
      {/* O TEXTO respeita a faixa; o TRILHO não — a mesma divisão das fileiras
          do diretório. É ela que dá o efeito de "tem mais vídeo ali fora": o
          primeiro card nasce alinhado com o título e o resto sangra até a borda
          da tela, sem parar num gutter no meio do caminho. */}
      <div
        className={cn(
          PUBLIC_CONTAINER,
          'flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between',
        )}
      >
        <div className="max-w-3xl">
          <span
            className={cn(
              'text-xs font-bold uppercase tracking-[0.14em]',
              copy.accentClass,
            )}
          >
            {copy.eyebrow}
          </span>

          <h2
            id={HEADING_ID}
            className="mt-3 text-2xl font-extrabold leading-tight tracking-tight text-gray-900 sm:text-3xl"
          >
            {copy.heading.dark}{' '}
            <span className={copy.accentClass}>{copy.heading.accent}</span>
          </h2>

          <p className="mt-3 text-base leading-relaxed text-gray-600">
            {copy.lead}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {/* Ao lado do link do canal, e não flutuando sobre os vídeos: sobre a
              capa a seta cobre justamente o que a pessoa veio ver. Só aparecem
              quando há para onde rolar. */}
          {hasControls && (
            <>
              <RailArrow
                direction="prev"
                disabled={!canScrollPrev}
                controls={RAIL_ID}
                noun="shorts"
                onClick={() => scrollByPage(-1)}
              />
              <RailArrow
                direction="next"
                disabled={!canScrollNext}
                controls={RAIL_ID}
                noun="shorts"
                onClick={() => scrollByPage(1)}
              />
            </>
          )}

          <a
            href={BEWAVE_YOUTUBE_SHORTS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-800 transition-colors hover:border-primary hover:text-primary"
          >
            Ver o canal no YouTube
            <ArrowRight className="size-4" aria-hidden="true" />
          </a>
        </div>
      </div>

      {/* Fila de rolagem nativa, e não grid: a lista cresce sem que a seção
          precise saber quantos vídeos cabem numa linha, e no celular a rolagem
          horizontal deixa claro que há mais vídeo depois do primeiro —
          empilhado, o segundo se perde fora da tela.

          `public-rail` (application.css) põe o gutter a partir da MESMA faixa de
          1760px do container e já traz o `scroll-padding` junto: sem ele o snap
          encostaria a capa na borda e comeria o gutter.

          Largura fixa por card, e não porcentagem como nas fileiras de escola:
          o short é 9/16, e um card percentual numa tela de 1760px viraria um
          retângulo de dois metros de altura. */}
      <ul
        ref={ref}
        {...dragProps}
        aria-label="Shorts do canal"
        className="public-rail mt-8 flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2 [scrollbar-width:none] sm:gap-5 [&::-webkit-scrollbar]:hidden"
      >
        {SHORTS.map((short) => (
          <li
            key={short.id}
            className="w-[15.5rem] shrink-0 snap-start sm:w-[16.5rem]"
          >
            <ShortCard
              short={short}
              playing={playingId === short.id}
              onPlay={() => setPlayingId(short.id)}
            />
          </li>
        ))}
      </ul>
    </section>
  )
}

interface ShortCardProps {
  short: Short
  playing: boolean
  onPlay: () => void
}

/**
 * Um short: capa com botão de play até o clique, iframe depois dele.
 *
 * O `aspect-[9/16]` é o que segura o layout — a moldura tem a altura final desde
 * o primeiro paint, então trocar a capa pelo player não empurra a página.
 */
function ShortCard({ short, playing, onPlay }: Readonly<ShortCardProps>) {
  return (
    <div className="relative aspect-[9/16] w-full overflow-hidden rounded-3xl bg-gray-900 shadow-[0_6px_24px_rgba(0,0,0,0.08)]">
      {playing ? (
        <iframe
          src={embedSrc(short.id)}
          title={short.title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          className="absolute inset-0 size-full border-0"
        />
      ) : (
        <button
          type="button"
          onClick={onPlay}
          aria-label={`Assistir: ${short.title}`}
          className="group absolute inset-0 size-full cursor-pointer"
        >
          {/* `width`/`height` são as da capa gerada, para o navegador saber a
              proporção antes de o arquivo chegar. */}
          <img
            src={coverSrc(short.id)}
            alt=""
            width={405}
            height={720}
            loading="lazy"
            className="size-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
          />

          {/* Véu de baixo para cima: dá contraste ao título sem lavar a imagem. */}
          <span className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/80 to-transparent" />

          <span className="absolute inset-0 flex items-center justify-center">
            <span className="flex size-14 items-center justify-center rounded-full bg-white/90 text-lp-blue shadow-lg transition-transform duration-200 group-hover:scale-110">
              <Play className="ml-0.5 size-6 fill-current" aria-hidden="true" />
            </span>
          </span>

          <span className="absolute inset-x-0 bottom-0 p-4 text-left text-[0.8125rem] font-semibold leading-[1.35] text-white">
            {short.title}
          </span>
        </button>
      )}
    </div>
  )
}
